import Color from 'color';
import { cn } from 'v5/platform/dom/cn';

export type EnterpriseTeamIconProps = {
  name: string;
  iconUrl: string;
  iconColor: string;
  className?: string;
};

type TeamIconSurface = {
  backgroundColor?: string;
  initialsClassName: string;
  useThemeFallback: boolean;
  invertSvg: boolean;
};

function resolveTeamIconSurface(iconColor: string): TeamIconSurface {
  const raw = iconColor.trim();
  if (!raw) {
    return {
      useThemeFallback: true,
      initialsClassName: 'text-fg-muted',
      invertSvg: false
    };
  }

  try {
    const background = Color(raw);
    const isLight = background.isLight();
    return {
      useThemeFallback: false,
      backgroundColor: background.hex(),
      initialsClassName: isLight ? 'text-black' : 'text-white',
      invertSvg: !isLight
    };
  } catch {
    return {
      useThemeFallback: true,
      initialsClassName: 'text-fg-muted',
      invertSvg: false
    };
  }
}

export function EnterpriseTeamIcon({
  name,
  iconUrl,
  iconColor,
  className
}: EnterpriseTeamIconProps) {
  const initials = name.substring(0, 2).toUpperCase();
  const surface = resolveTeamIconSurface(iconColor);

  return (
    <div
      className={cn(
        'w-6 h-6 rounded-sm overflow-hidden flex items-center justify-center shrink-0',
        surface.useThemeFallback && 'bg-bg-subtle ring-1 ring-border-muted',
        className
      )}
      style={
        surface.backgroundColor
          ? { backgroundColor: surface.backgroundColor }
          : undefined
      }
    >
      {iconUrl ? (
        <img
          src={iconUrl}
          alt=""
          className={cn(
            'size-4 object-contain',
            iconUrl.endsWith('.svg')
              ? surface.invertSvg
                ? 'invert'
                : 'brightness-0'
              : 'object-cover size-full'
          )}
        />
      ) : (
        <span className={cn('text-[7pt] font-bold', surface.initialsClassName)}>
          {initials}
        </span>
      )}
    </div>
  );
}
